import dayjs from "dayjs";
import duration from "dayjs/plugin/duration.js";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter.js";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore.js";
import localizedFormat from "dayjs/plugin/localizedFormat.js";
import relativeTime from "dayjs/plugin/relativeTime.js";
import utc from "dayjs/plugin/utc.js";
import weekOfYear from "dayjs/plugin/weekOfYear.js";
dayjs.extend(weekOfYear);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
export function toDayjs(date) {
    const firestoreTimestamp = date;
    if (firestoreTimestamp?.seconds) {
        return dayjs(firestoreTimestamp.seconds * 1000 +
            firestoreTimestamp.nanoseconds / 1000000);
    }
    let dayJs;
    const numericValue = typeof date === "number"
        ? date
        : typeof date === "string"
            ? Number(date)
            : null;
    if (numericValue !== null && !isNaN(numericValue) && isFinite(numericValue)) {
        // Handle string numbers as Unix timestamps
        // If the number is likely in seconds (< year 2100), convert to milliseconds
        // Unix timestamp in seconds: 1721484000 (July 2024)
        // Unix timestamp in milliseconds: 1721484000000 (July 2024)
        // Cutoff: 4102444800 (Jan 1, 2100 in seconds)
        const timestamp = numericValue < 4102444800 ? numericValue * 1000 : numericValue;
        // Use UTC mode for Unix timestamps as they represent absolute time in UTC
        // This ensures correct date/time values regardless of local timezone
        dayJs = dayjs.utc(timestamp);
    }
    else if (typeof date === "string" &&
        !date.includes("Z") &&
        !date.includes("+")) {
        dayJs = dayjs(date + "Z");
    }
    else {
        dayJs = dayjs(date);
    }
    if (dayJs.isValid()) {
        return dayJs;
    }
    throw new Error(`Invalid date: ${date}`);
}
export function toNativeDate(date) {
    return toDayjs(date).toDate();
}
export function toDateWithOffset(offsetSeconds, localDate) {
    return addDuration(localDate || new Date(), offsetSeconds, "second");
}
export function addDuration(date, value, unit) {
    return toDayjs(date).add(value, unit).toDate();
}
export function getWeek(date) {
    return toDayjs(date).week();
}
export function getDate(date) {
    return toDayjs(date).date();
}
export function getHour(date) {
    return toDayjs(date).hour();
}
export function getMinute(date) {
    return toDayjs(date).minute();
}
export function setDateOfMonth(date, dateOfMonth) {
    return toDayjs(date).date(dateOfMonth).toDate();
}
export function setDayOfWeek(date, dateOfWeek) {
    return toDayjs(date).day(dateOfWeek).toDate();
}
export function setHour(date, hour) {
    return toDayjs(date).hour(hour).toDate();
}
export function setMinute(date, minute) {
    return toDayjs(date).minute(minute).toDate();
}
export function setSecond(date, second) {
    return toDayjs(date).second(second).toDate();
}
export function setMillisecond(date, millisecond) {
    return toDayjs(date).millisecond(millisecond).toDate();
}
export function isAfter(date1, date2) {
    return toDayjs(date1).isAfter(toDayjs(date2));
}
export function isBefore(date1, date2) {
    return toDayjs(date1).isBefore(toDayjs(date2));
}
export function isAfterOrEqual(date1, date2) {
    return toDayjs(date1).isSameOrAfter(toDayjs(date2));
}
export function isBeforeOrEqual(date1, date2) {
    return toDayjs(date1).isSameOrBefore(toDayjs(date2));
}
export function makeTimestamp(seconds, nanoseconds) {
    return {
        seconds,
        nanoseconds,
        toMillis: () => seconds * 1000 + Math.floor(nanoseconds / 1e6),
        toDate: () => new Date(seconds * 1000 + nanoseconds / 1e6),
        fromDate: (date) => makeTimestamp(Math.floor(date.getTime() / 1000), (date.getTime() % 1000) * 1e6),
    };
}
export function diffIn(date1, date2, unit) {
    return toDayjs(date1).diff(toDayjs(date2), unit, true);
}
export function startOf(date1, unit) {
    return toDayjs(date1).startOf(unit).toDate();
}
export function endOf(date1, unit) {
    return toDayjs(date1).endOf(unit).toDate();
}
export function formatDate(date, format) {
    if (!date)
        return "";
    const dayjsDate = toDayjs(date);
    // Convert UTC mode dayjs objects to local timezone for display
    // This ensures Unix timestamps are parsed correctly in UTC but displayed in local time
    return dayjsDate.isUTC()
        ? dayjsDate.local().format(format)
        : dayjsDate.format(format);
}
export function formatDistance(date1, date2) {
    return toDayjs(date1).from(toDayjs(date2));
}
export function humanizeDuration(value, unit, suffix = true) {
    return dayjs.duration(value, unit).humanize(suffix);
}
export function distanceToNow(date) {
    if (!date)
        return "";
    return toDayjs(date).toNow();
}
export function distanceFromNow(date) {
    if (!date)
        return "";
    return toDayjs(date).fromNow();
}
export function startOfPeriod(period) {
    switch (period) {
        case "1w":
            return Date.now() - 7 * 24 * 60 * 60 * 1000;
        case "2w":
            return Date.now() - 14 * 24 * 60 * 60 * 1000;
        case "1m":
            return Date.now() - 30 * 24 * 60 * 60 * 1000;
        case "3m":
            return Date.now() - 91 * 24 * 60 * 60 * 1000;
        case "6m":
            return Date.now() - 182 * 24 * 60 * 60 * 1000;
        case "YTD":
            return dayjs().startOf("year").valueOf();
        case "1y":
            return Date.now() - 365 * 24 * 60 * 60 * 1000;
        default:
            return null;
    }
}
export function recentYears(yearCount = 1) {
    return [...Array(yearCount)].map((_, idx) => {
        return dayjs().subtract(idx, "years").format("YYYY");
    });
}
export function preserveTime(source, target) {
    const sourceDate = toNativeDate(source);
    const targetDate = toNativeDate(target);
    targetDate.setHours(sourceDate.getHours(), sourceDate.getMinutes(), sourceDate.getSeconds(), sourceDate.getMilliseconds());
    return targetDate;
}
